<template>
    <div class="employee-time-usage">
        
        <div class="row">

            <div class="col-lg-4">

                <div class="placeholder fit-content">
                    <div class="placeholder__heading">
                        Medarbejdere
                    </div>
                    <div class="placeholder">
                        
                        <vue-good-table
                            :columns="columns"
                            :rows="rows"
                            :search-options="searchOptions"
                            :pagination-options="paginationOptions"
                            :sort-options="sortOptions"
                            :styleClass="styleClass"
                            @on-row-click="selectCustomer"
                            :row-style-class="selectedRowClass"
                            v-if="!loading"
                        >
                            <template slot="table-row" slot-scope="props">
                                <div v-if="props.column.field == 'hoursWithCustomers'">
                                    {{ props.row.hoursWithCustomers }} %
                                </div>
                                <div v-else-if="props.column.field == 'sicknessPercent'">
                                    {{ props.row.sicknessPercent }} %
                                </div>
                            </template>

                        </vue-good-table>

                        <box-loading height="350" v-else />

                    </div>
                </div>

            </div>

            <div class="col-lg-8">

                <div class="placeholder fit-content" v-if="selectedRow && !loading">
                    <div class="placeholder__heading">
                        <d-tooltip placement="left" identifier="employees.timeUsage.singleEmployee" />
                        {{ selectedRow.name }}
                    </div>
                    <div class="placeholder padding">

                        <div class="row">
                            <div class="col-lg-6">
                                <pie
                                    :headline="'Hvad bruger ' + selectedRow.name.split(' ')[0] + ' tiden på?'"
                                    :series="selectedRow.hours_division.series"
                                    :labels="selectedRow.hours_division.labels"
                                    :division="true"
                                    v-if="selectedRow.hours_division.series[0] > 0 && selectedRow !== null"
                                />
                                <div v-if="selectedRow.hours_division.series[0] === 0">
                                    <div class="headline" style="text-align:center;font-weight:600;font-family:'Open Sans', sans-serif;">
                                        Hvad bruger {{ selectedRow.name.split(' ')[0] }} tiden på?
                                    </div>
                                    <div style="text-align:center;">
                                        <br>
                                        Ingen tid hos kunderne
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <pie
                                    headline="Fordeling af tid hos kunderne"
                                    :series="selectedRow.hours_with_customers.series"
                                    :labels="selectedRow.hours_with_customers.labels"
                                    :division="false"
                                    v-if="selectedRow.hours_with_customers.labels.length > 0 && selectedRow !== null"
                                />
                                <div v-if="selectedRow.hours_with_customers.labels.length === 0">
                                    <div class="headline" style="text-align:center;font-weight:600;font-family:'Open Sans', sans-serif;">
                                        Fordeling af tid hos kunderne
                                    </div>
                                    <div style="text-align:center;">
                                        <br>
                                        Ingen tid hos kunderne
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="divider"></div>

                        <div class="row">
                            <div class="col-lg-6">
                                <pie
                                    headline="Fordeling af anden tid"
                                    :series="selectedRow.hours_without_customers.series"
                                    :labels="selectedRow.hours_without_customers.labels"
                                    :division="false"
                                    v-if="selectedRow.hours_without_customers.labels.length > 0"
                                />
                                <div v-else>
                                    <div class="headline" style="text-align:center;font-weight:600;font-family:'Open Sans', sans-serif;">
                                        Fordeling af anden tid
                                    </div>
                                    <div style="text-align:center;">
                                        <br>
                                        Ingen anden tid registreret
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <pie
                                    headline="Fordeling af fravær"
                                    :series="selectedRow.hours_excluded.series"
                                    :labels="selectedRow.hours_excluded.labels"
                                    :division="false"
                                    v-if="selectedRow.hours_excluded && selectedRow.hours_excluded.labels.length > 0"
                                />
                                <div v-else>
                                    <div class="headline" style="text-align:center;font-weight:600;font-family:'Open Sans', sans-serif;">
                                        Fordeling af fravær
                                    </div>
                                    <div style="text-align:center;">
                                        <br>
                                        Intet fravær registreret
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="footnote">
                            <strong>Tid brugt hos kunderne</strong> er beregnet ud af medarbejderens reelle tid til rådighed: den teoretiske arbejdstid (arbejdsdage × timer/uge) fratrukket fraværstimer. <strong>Fravær</strong> er de tidsregistreringstyper, der er sat til "Skjult" under <router-link to="/indstillinger">Indstillinger &rarr; Tidsregistreringstyper</router-link> — de fratrækkes tid til rådighed og tæller derfor ikke med i kundetidsprocenten.
                        </div>

                        <div style="display:none;">
                            <br><br>
                            <b>selectedRow</b><br>
                            labels: {{ selectedRow.hours_without_customers.labels }}<br>
                            series: {{ selectedRow.hours_without_customers.series }}
                            <br><br>
                            <b>Allan Jensen</b><br>
                            labels: {{ rows[1].hours_without_customers.labels }}<br>
                            series: {{ rows[1].hours_without_customers.series }}
                        </div>

                    </div>
                </div>

                <div v-else>
                    Vælg en svend for at se fordelingen af hans registrerede timer
                </div>

            </div>

        </div>

    </div>
</template>

<style lang="scss" scoped>
@import "../../../../styles/vars.scss";

    .footnote {
        margin-top: $margin--main;
        padding-top: $margin--small;
        border-top: 1px solid $color--light;
        font-size: $fontsize--small;
        color: $color--grey;
        line-height: 1.5;

        a {
            color: $color--main;
            text-decoration: underline;
        }
    }

</style>

<script>
import VueApexCharts from 'vue-apexcharts'
import * as timeUsageService from '@/services/timeUsageService'

export default {
    props: ['employeeWorkingFieldName'],
    data () {
        return {
            loading: true,
            selectedRow: null,
            rows: [],
            columns: [
                { label: 'Navn', field: 'name', type: 'string' },
                { label: 'Tid hos kunder', field: 'hoursWithCustomers', type: 'number' },
                { label: 'Sygefravær', field: 'sicknessPercent', type: 'number' }
            ],
            searchOptions: {
                enabled: true,
                trigger: 'enter',
                placeholder: 'Søg og tryk enter ...',
                skipDiacritics: true
            },
            paginationOptions: {
                enabled: true,
                perPage: 15,
                rowsPerPageLabel: 'Rækker pr. side',
                allLabel: 'Alle',
                nextLabel: 'Næste',
                prevLabel: 'Forrige',
                ofLabel: 'af',
                pageLabel: 'side'
            },
            sortOptions: {
                enabled: true,
                initialSortBy: {
                    field: 'hoursWithCustomers',
                    type: 'desc'
                }
            },
            styleClass: 'vgt-table'
        }
    },
    created () {
        this.getData()
    },
    methods: {
        getData () {
            this.loading = true
            this.rows = []
            this.selectedRow = null
            let params = {
                date_from: this.$store.getters.getStartDate,
                date_to: this.$store.getters.getEndDate
            }
            if (this.$store.getters.getCurrentDepartment.uuid !== 0) {
                params.department = this.$store.getters.getCurrentDepartment.uuid
            }
            if (this.employeeWorkingFieldName !== '') {
                params.working_field_name = this.employeeWorkingFieldName
            }
            timeUsageService.employees(params, (response) => {
                console.log(response.data)
                response.data.forEach((item) => {
                    let hoursWithCustomers = item.hours_division.series[0]
                    let totalDivision = item.hours_division.series.reduce((a, b) => a + b, 0)
                    item.hoursWithCustomers = totalDivision > 0 ? Math.round((hoursWithCustomers / totalDivision) * 100) : 0
                    let hours = 0
                    let totalHours = 0
                    item.hours_without_customers.series.forEach((series) => {
                        totalHours = totalHours + series
                    })
                    item.hours_without_customers.labels.forEach((label, index) => {
                        if (label.toString(16).toLowerCase().includes('syg')) {
                            hours = hours + item.hours_without_customers.series[index]
                        }
                    })
                    item.sicknessPercent = hours > 0 && totalHours > 0 ? Math.round((hours / item.available_hours) * 100) : 0
                })
                this.rows = response.data
                this.loading = false
            }, (err) => {
                console.log(err)
                this.loading = false
            })
        },
        selectCustomer (employee) {
            this.selectedRow = null
            setTimeout(() => {
                this.selectedRow = employee.row
            }, 250)
        },
        selectedRowClass (row) {
            if (this.selectedRow && row.uuid === this.selectedRow.uuid) {
                return 'selected'
            }
        }
    },
    components: {
        VueApexCharts,
        'hours-with-customers': require('./hoursWithCustomers.vue'),
        'pie': require('./pie.vue')
    },
    computed: {
        globalParams () {
            let startDate = this.$store.getters.getStartDate
            let endDate = this.$store.getters.getEndDate
            let department = this.$store.getters.getCurrentDepartment.uuid
            return startDate + endDate + department
        }
    },
    watch: {
        globalParams: function () {
            this.getData()
        },
        employeeWorkingFieldName: function () {
            this.getData()
        }
    }
}
</script>



// WEBPACK FOOTER //
// employeeTimeUsage.vue?2822831f